import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import * as LucideIcons from 'lucide-react';
import { useTranslation } from 'react-i18next';

const MusicPlayer = () => {
    const { t } = useTranslation();
    const audioRef = useRef(null);
    const [volume, setVolume] = useState(0.1);
    const [isAutoplayEnabled, setIsAutoplayEnabled] = useState(() => {
        return localStorage.getItem('musicAutoplay') === 'true';
    });
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [hasInteracted, setHasInteracted] = useState(false);

    const playAudio = useCallback(() => {
        if (audioRef.current) {
            audioRef.current.play().catch((e) => console.log("Audio playback requires user interaction."));
        }
    }, []);

    useEffect(() => {
        const handleInteraction = () => {
            if (!hasInteracted) {
                setHasInteracted(true);
                if (isAutoplayEnabled) {
                    setIsPlaying(true);
                    playAudio();
                }
            }
            window.removeEventListener('click', handleInteraction);
            window.removeEventListener('keydown', handleInteraction);
        };

        window.addEventListener('click', handleInteraction);
        window.addEventListener('keydown', handleInteraction);

        return () => {
            window.removeEventListener('click', handleInteraction);
            window.removeEventListener('keydown', handleInteraction);
        };
    }, [hasInteracted, playAudio, isAutoplayEnabled]);


    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden) {
                audioRef.current?.pause();
            } else if (isPlaying) {
                playAudio();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [isPlaying, playAudio]);

    useEffect(() => {
        if (audioRef.current) {
            if (isPlaying) playAudio();
            else audioRef.current.pause();
        }
    }, [isPlaying, playAudio]);

    useEffect(() => { if (audioRef.current) { audioRef.current.volume = volume; audioRef.current.muted = isMuted; } }, [volume, isMuted]);
    const handleVolumeChange = useCallback((e) => { const newVolume = parseFloat(e.target.value); setVolume(newVolume); if (newVolume > 0) setIsMuted(false); }, []);
    const handlePlayPauseToggle = useCallback(() => setIsPlaying(prev => !prev), []);
    const handleMuteToggle = useCallback(() => setIsMuted(prev => !prev), []);

    const handleAutoplayToggle = useCallback(() => {
        const newAutoplayState = !isAutoplayEnabled;
        setIsAutoplayEnabled(newAutoplayState);
        localStorage.setItem('musicAutoplay', newAutoplayState);
    }, [isAutoplayEnabled]);

    const VolumeIcon = useMemo(() => { if (isMuted || volume === 0) return LucideIcons.VolumeX; if (volume < 0.5) return LucideIcons.Volume1; return LucideIcons.Volume2; }, [isMuted, volume]);

    return (
        <div className="fixed top-4 left-4 p-2 bg-gray-800 bg-opacity-50 rounded-full flex items-center space-x-2" style={{ zIndex: 900 }}>
            <audio ref={audioRef} src="https://github.com/Jelani0/WhispersOfHarmony/raw/refs/heads/main/melodyloops-touch-of-hope.mp3" loop />
            <button
                onClick={handleAutoplayToggle}
                className={`p-2 rounded-full text-white transition-colors duration-300 ${isAutoplayEnabled ? 'bg-green-500 hover:bg-green-600' : 'bg-gray-600 hover:bg-gray-500'}`}
                title={isAutoplayEnabled ? t('musicPlayer.disableAutoplay') : t('musicPlayer.enableAutoplay')}
            >
                <LucideIcons.PlayCircle size={20} />
            </button>
            <button onClick={handlePlayPauseToggle} className="p-2 rounded-full bg-blue-500 hover:bg-blue-600 text-white transition-colors duration-300">{isPlaying ? <LucideIcons.Pause size={20} /> : <LucideIcons.Play size={20} />}</button>
            <button onClick={handleMuteToggle} className="p-2 rounded-full text-white hover:bg-white/10 transition-colors duration-300"><VolumeIcon size={20} /></button>
            <input type="range" min="0" max="1" step="0.01" value={volume} onChange={handleVolumeChange} className="w-20 h-1 rounded-lg appearance-none cursor-pointer bg-gray-700 accent-blue-500" title={t('musicPlayer.volumeControl')} />
        </div>
    );
};

export default MusicPlayer;